import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { owner, paypal as pp } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Check, Sparkles, Zap, Award, Loader2, Info, Star, Building2, ArrowRight, ArrowDown } from 'lucide-react';
import PayPalTrustBadge from '../components/PayPalTrustBadge';
import SiteSections from '../components/SiteSections';
import { PlanCompareTable } from '../components/PlanCompareTable';

const ICONS = { basic: Award, standard: Zap, featured: Sparkles };
const ORDER = ['basic', 'standard', 'featured'];
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

export default function UpgradePlan() {
  const navigate = useNavigate();
  const plansRef = useRef(null);
  const [packages, setPackages] = useState([]);
  const [myBiz, setMyBiz] = useState([]);
  const [selectedBiz, setSelectedBiz] = useState('');
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openPkg, setOpenPkg] = useState(null);

  const load = () =>
    Promise.all([owner.packages(), owner.myBusinesses(), owner.myPayments()]).then(([p, b, pay]) => {
      // Sort into the display order asked for: listing → growth → campaign
      const sorted = ORDER.map(id => p.find(x => x.id === id)).filter(Boolean);
      setPackages(sorted); setMyBiz(b); setPayments(pay);
      if (b.length && !selectedBiz) setSelectedBiz(b[0].id);
    });

  useEffect(() => { load().finally(() => setLoading(false)); /* eslint-disable-next-line */ }, []);

  const current = myBiz.find(b => b.id === selectedBiz);
  const currentPkg = current?.package || 'none';
  const activeUntil = current?.packageExpiresAt ? new Date(current.packageExpiresAt) : null;
  const isActive = activeUntil && activeUntil > new Date();

  if (loading) return <div className="flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={16}/> Loading...</div>;

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="pbb-card p-6 flex items-start gap-4 flex-wrap bg-gradient-to-br from-[#0d1f14] to-[#0a1a0f] border-green-500/30">
        <div className="flex-1 min-w-[240px]">
          <h1 className="text-2xl font-bold">Business Packages</h1>
          <p className="text-gray-400 text-sm mt-1">Three tiers, one clear path — <b className="text-white">GET FOUND</b> → <b className="text-white">PARTICIPATE & EARN</b> → <b className="text-white">GROW & AUTOMATE</b>.</p>
        </div>
        <button
          type="button"
          data-testid="upgrade-choose-plan"
          onClick={() => plansRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-green-500 hover:bg-green-400 text-black font-bold shadow-lg shadow-green-500/20">
          Choose a Plan <ArrowDown size={16}/>
        </button>
      </div>

      {myBiz.length === 0 ? (
        <div className="pbb-card p-10 text-center border-2 border-dashed border-green-500/40 bg-green-500/5" data-testid="upgrade-empty-state">
          <Building2 className="mx-auto text-green-400" size={32}/>
          <div className="mt-3 text-lg font-bold">Create your business listing first</div>
          <p className="text-sm text-gray-300 mt-2 max-w-md mx-auto">Every plan is tied to a listing. Register your business with the free Category Listing form — then come back here to upgrade to Business Growth or Full Automation.</p>
          <Link to="/business/manage" data-testid="upgrade-empty-cta" className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-green-600 hover:bg-green-500 text-white text-sm font-semibold">
            Register your business <ArrowRight size={14}/>
          </Link>
        </div>
      ) : (
        <>
          <PayPalTrustBadge />
          <div className="pbb-card p-5 flex items-center gap-4 flex-wrap">
            <div className="text-sm text-gray-400">Purchase for:</div>
            <select className="pbb-input" style={{ maxWidth: 300 }} value={selectedBiz} onChange={e => setSelectedBiz(e.target.value)}>
              {myBiz.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            {current && (
              <div className="ml-auto text-sm">
                <span className="text-gray-400">Current: </span>
                <span className="px-2 py-0.5 rounded text-xs uppercase tracking-wider bg-green-600/15 text-green-400 border border-green-600/30">
                  {isActive ? (packages.find(p => p.id === currentPkg)?.name || currentPkg) : 'not active'}
                </span>
                {isActive && <span className="ml-3 text-gray-500 text-xs">expires {activeUntil.toLocaleDateString()}</span>}
              </div>
            )}
          </div>

          {/* Package cards */}
          <div ref={plansRef} className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="pkg-grid">
            {packages.map(t => {
              const Ic = ICONS[t.id] || Award;
              const isCurrent = isActive && currentPkg === t.id;
              const highlight = t.recommended;
              return (
                <div key={t.id} data-testid={`pkg-card-${t.id}`} className={`pbb-card p-6 flex flex-col relative ${highlight ? 'ring-2 ring-green-500 shadow-[0_0_40px_rgba(34,197,94,0.15)]' : ''}`}>
                  {highlight && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-green-500 text-black text-[10px] uppercase tracking-wider font-bold inline-flex items-center gap-1">
                      <Star size={10} className="fill-black"/> Recommended
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Ic className="text-green-400" size={22}/>
                    <div className="text-lg font-semibold uppercase tracking-tight">{t.name}</div>
                  </div>
                  {t.tagline && <div className="text-xs uppercase tracking-widest text-green-400 mt-1">{t.tagline}</div>}
                  {t.description && <p className="text-sm text-gray-400 mt-2">{t.description}</p>}
                  <div className="mt-4 border-t border-[#1f1f1f] pt-4">
                    {t.regularPrice && t.regularPrice !== t.price ? (
                      <>
                        <div className="text-[10px] uppercase tracking-wider text-yellow-400 font-bold">Intro offer</div>
                        <div><span className="text-3xl font-bold text-green-400">${t.price}</span><span className="text-sm text-gray-400"> for {t.months} months</span></div>
                        <div className="text-xs text-gray-400 mt-1">Then <b className="text-white">${t.regularPrice} / month</b></div>
                      </>
                    ) : t.id === 'basic' ? (
                      <>
                        <div><span className="text-3xl font-bold text-green-400">${t.price}</span><span className="text-sm text-gray-400"> / month</span></div>
                        <div className="text-xs text-gray-400 mt-1">Monthly recurring</div>
                      </>
                    ) : (
                      <>
                        <div><span className="text-3xl font-bold text-green-400">${t.price}</span><span className="text-sm text-gray-400"> for {t.months} months</span></div>
                        <div className="text-xs text-gray-400 mt-1">Then <b className="text-white">${t.price} / month</b></div>
                      </>
                    )}
                  </div>
                  <ul className="mt-4 space-y-1.5 text-sm text-gray-300 flex-1">
                    {t.features.slice(0, 8).map(f => (
                      <li key={f} className="flex items-start gap-2"><Check size={14} className="text-green-400 mt-0.5 shrink-0"/> {featureLabel(f)}</li>
                    ))}
                    {t.features.length > 8 && (
                      <li className="text-xs text-green-400 pl-6">+ {t.features.length - 8} more — see compare table below</li>
                    )}
                  </ul>
                  {t.footerMessage && (
                    <div className="mt-4 text-[11px] italic text-gray-400 border-l-2 border-green-500 pl-2">{t.footerMessage}</div>
                  )}
                  <button onClick={() => setOpenPkg(t.id)} data-testid={`pkg-cta-${t.id}`}
                    className={`mt-5 w-full px-3 py-2.5 rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-2 ${highlight ? 'bg-green-500 hover:bg-green-600 text-black' : 'bg-green-600 hover:bg-green-700 text-white'}`}>
                    {isCurrent ? `Renew — $${t.price}` : (t.cta || `Buy for $${t.price}`)}
                  </button>
                </div>
              );
            })}
          </div>

          <PlanCompareTable />

          <div className="pbb-card p-4 text-sm text-gray-400 flex items-start gap-2">
            <Info size={16} className="text-green-400 mt-0.5 shrink-0"/>
            <div>Payments run through PayPal (sandbox mode during testing — no real money moves). Intro periods activate for 3 months; monthly plans renew each month.</div>
          </div>

          {payments.length > 0 && (
            <div className="pbb-card p-5">
              <div className="font-semibold mb-3">Payment History</div>
              <ul className="divide-y divide-[#1f1f1f] text-sm">
                {payments.map(p => (
                  <li key={p.id} className="flex items-center gap-3 py-2">
                    <div className="flex-1">
                      <div className="text-gray-200">{packages.find(x => x.id === p.package)?.name || p.package}</div>
                      <div className="text-xs text-gray-500">{new Date(p.createdAt).toLocaleString()} • expires {new Date(p.expiresAt).toLocaleDateString()}</div>
                    </div>
                    <div className="text-green-400 font-medium">${p.amount}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {openPkg && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setOpenPkg(null)}>
          <div className="pbb-card w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="checkout-modal">
            <div className="font-semibold text-lg">Checkout</div>
            {(() => {
              const t = packages.find(p => p.id === openPkg);
              if (!t) return null;
              return (
                <>
                  <div className="text-sm mt-2">{t.name}</div>
                  <div className="mt-2 rounded-lg bg-[#0d1f14] border border-[#153b23] p-3 text-sm space-y-1">
                    <div className="flex justify-between"><span className="text-gray-400">Pay now:</span><b className="text-green-400">${t.price}</b></div>
                    {t.regularPrice && t.regularPrice !== t.price && (
                      <div className="flex justify-between text-xs"><span className="text-gray-400">After {t.months} months:</span><b>${t.regularPrice} / mo</b></div>
                    )}
                    {t.id === 'basic' && (
                      <div className="flex justify-between text-xs"><span className="text-gray-400">Renewal:</span><b>${t.price} / mo</b></div>
                    )}
                    {t.id !== 'basic' && (!t.regularPrice || t.regularPrice === t.price) && (
                      <div className="flex justify-between text-xs"><span className="text-gray-400">Renewal:</span><b>${t.price} / mo</b></div>
                    )}
                  </div>
                </>
              );
            })()}
            <div className="mt-5">
              <PayPalScriptProvider options={{ 'client-id': PAYPAL_CLIENT_ID, currency: 'USD', intent: 'capture' }}>
                <PayPalButtons
                  style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'paypal' }}
                  createOrder={async () => {
                    try { const res = await pp.createOrder(openPkg, selectedBiz); return res.orderId; }
                    catch (err) { toast({ title: 'PayPal error', description: err?.response?.data?.detail || 'Try again' }); throw err; }
                  }}
                  onApprove={async (data) => {
                    try {
                      await pp.capture(data.orderID, openPkg, selectedBiz);
                      toast({ title: 'Payment complete!', description: 'Redirecting to Manage Listings…' });
                      setOpenPkg(null);
                      await load();
                      // Give the toast a beat, then send them to Manage Listings.
                      setTimeout(() => navigate('/business/manage'), 800);
                    } catch (err) { toast({ title: 'Capture failed', description: err?.response?.data?.detail || 'Try again' }); }
                  }}
                  onError={(err) => toast({ title: 'PayPal error', description: String(err?.message || err) })}
                />
              </PayPalScriptProvider>
            </div>
            <button onClick={() => setOpenPkg(null)} className="mt-4 w-full px-3 py-2 rounded-lg border border-[#2a2a2a] text-sm text-gray-300">Cancel</button>
          </div>
        </div>
      )}
      <SiteSections pageKey="upgrade" />
    </div>
  );
}

function featureLabel(f) {
  const map = {
    businesses_1: '1 business, 1 category listing',
    businesses_2: 'Up to 2 businesses',
    businesses_4: 'Up to 4 businesses',
    categories_3: 'Up to 3 category listings per business',
    categories_4: 'Up to 4 category listings per business',
    business_photos_3: '3 business photos per listing',
    links_3: '3 links per listing (website + social media combined)',
    business_photos_unlimited: 'Unlimited business photos',
    website_and_social_unlimited: 'Unlimited website + social links',
    signup_form: 'Custom sign-up form',
    survey_builder: 'Customer survey',
    directory_listing: 'Directory listing',
    business_category: 'Business category',
    business_name: 'Business name',
    contact_info: 'Contact information',
    location: 'Location',
    business_photo_1: '1 Business photo',
    website_or_social_1: '1 Website or social link',
    business_description: 'Business description',
    business_hours: 'Business hours',
    create_coupons: 'Create coupons',
    online_market: 'Participate in Online Market',
    create_events: 'Create events',
    pbb_promotions: 'Participate in PBB promotions',
    loyalty_card: 'Loyalty card participation',
    pbb_points: 'Earn PBB points',
    consumer_engagement: 'Engage with consumers',
    merchant_crm: 'Merchant CRM',
    customer_database: 'Customer database',
    customer_segmentation: 'Customer segmentation',
    customer_activity_tracking: 'Customer activity tracking',
    ai_customer_insights: 'AI customer insights',
    ai_email_sms: 'AI-generated email + SMS campaigns',
    automation: 'Automated email + SMS marketing',
    welcome_campaigns: 'Welcome campaigns',
    birthday_campaigns: 'Birthday campaigns',
    winback_campaigns: 'Win-back campaigns',
    loyalty_reactivation: 'Loyalty & reactivation campaigns',
    can_customer_tracking: 'CAN customer interaction tracking',
    advanced_analytics: 'Advanced analytics',
    // legacy fallbacks
    '1_coupon': '1 Coupon',
    '1_loyalty': '1 Loyalty program',
    '1_store_item': '1 Store item',
    priority_directory: 'Priority directory placement',
    events: 'Event calendar access',
    featured_placement: 'Featured placement everywhere',
    landing_page: 'Branded landing page',
    customer_list: 'Customer / CRM list',
    marketing_campaigns: 'Automated marketing campaigns',
    ai_assistant: 'AI marketing assistant',
    analytics: 'Marketing analytics',
  };
  return map[f] || f;
}
